import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { InternalTaskDTO, WorkTaskStatus } from '../../../api/api';
import Table from '../../../blocks/table/TableClient';
import TableMenuBar from '../../../blocks/table/table-menu-bar/TableMenuBar';
import InternalTaskService from '../../../services/InternalTaskService';
import { setShouldUpdateNumberOfInternalTasksCreated } from '../../../stateManagement/reducers/taskListReducer';
import { getEnumDisplayValue } from '../../../utils/enumUtils';
import InternalTaskChangeStatusMenu from './internal-task-list-tools/InternalTaskChangeStatusMenu';
import InternalTaskPlanningSystemMenu from './internal-task-list-tools/InternalTaskPlanningSystemMenu';
import { useInternalTaskList } from './useInternalTaskList';

interface Props {
  shouldRefresh: boolean;
  setShouldRefresh: (value: boolean) => void;
}

const InternalTaskList = (props: Props) => {
  const { shouldRefresh, setShouldRefresh } = props;
  const [tasks, setTasks] = useState<InternalTaskDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [showBookButton, setShowBookButton] = useState(false);
  const dispatch = useDispatch();

  const fetchTasks = useCallback(() => {
    setLoading(true);
    InternalTaskService.getInternalTasks()
      .then((res) => setTasks(res))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  useEffect(() => {
    if (shouldRefresh) fetchTasks();
  }, [fetchTasks, shouldRefresh]);

  const { tableInstance } = useInternalTaskList(tasks);

  const { toggleAllRowsSelected, selectedFlatRows } = tableInstance;

  useEffect(() => {
    if (selectedFlatRows.length > 0) {
      setShowBookButton(
        selectedFlatRows.every(
          (row) =>
            row.original.status === getEnumDisplayValue(WorkTaskStatus.Created) ||
            row.original.status === getEnumDisplayValue(WorkTaskStatus.Pause)
        )
      );
    }
  }, [selectedFlatRows]);

  const handleClose = useCallback(() => {
    toggleAllRowsSelected(false);
  }, [toggleAllRowsSelected]);

  const getExcelExportFileName = useCallback(() => {
    return `WORK opgaveliste interne - ${new Date().toLocaleDateString('da-DK', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })}.csv`;
  }, []);

  return (
    <Table
      loading={loading}
      tableInstance={tableInstance}
      multipleTypeName="interne opgaver"
      showPagination
      showCount
      showExcelExport
      excelExportFileNameFunction={getExcelExportFileName}
      onUpdateTable={() => setShouldRefresh(true)}
      showUpdateTable
      tableMenuBar={
        <TableMenuBar
          selectedItems={tableInstance.selectedFlatRows.map((x) => ({ id: x.id }))}
          rowNameSingle="opgave"
          rowNameMultiple="opgaver"
          onClose={handleClose}
        >
          <InternalTaskChangeStatusMenu
            fetchTasksCallback={fetchTasks}
            selectedRows={tableInstance.selectedFlatRows.map((r) => ({
              id: r.original.id ?? -1,
              hasSubTasks: false,
              status: r.original.status
            }))}
            setIsLoading={(isLoading: boolean) => setLoading(isLoading)}
          />
          {showBookButton && (
            <InternalTaskPlanningSystemMenu
              selectedTaskIds={tableInstance.selectedFlatRows.map((r) => r.original.id ?? -1)}
              fetchTasksCallback={fetchTasks}
              setIsLoading={(isLoading: boolean) => setLoading(isLoading)}
            />
          )}
        </TableMenuBar>
      }
    />
  );
};

export default InternalTaskList;
